import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { DataTable } from "@/components/ui/datatable";
import DateRangePicker from "@/components/ui/date-range-picker";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FancyMultiSelect } from "@/components/ui/fancy-multi-select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormError } from "@/components/ui/form-error";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { display_user } from "@/components/utils/display";
import { NavigationStatus } from "@/components/utils/navigation-status";
import { WithIcon } from "@/components/utils/with-icon";
import { secondsToHMS } from "@/pages/registration/external_registration/utils";
import { SFCase } from "@/types/salesforce/case";
import { UserType } from "@/types/user";
import { ActionWrapper } from "@/utils/wretch";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExternalLink } from "lucide-react";
import { DateTime } from "luxon";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useFetcher, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as z from "zod";
import { DiabolocomInjectionBatch } from "./types";
import { DatePicker } from "@/components/ui/date-picker";
import { Separator } from "@/components/ui/separator";
import { ColumnDef } from "@tanstack/react-table";
import { SFWalterSessionType } from "@/types/salesforce/walter_session";
import { cn } from "@/lib/utils";
import PercentageInput from "@/components/ui/input-percentage";

type Module = SFWalterSessionType & { parent: never };
type SFCaseWithModules = SFCase & { ac1: Module | null; fc: Module | null; ac2: Module | null; cv: Module | null };
type SFCaseLoggedTimeRow = {
    case: SFCaseWithModules;
    to_inject: boolean;
    comment: string;
    invoice_status?: string | null;
    amount_to_recover?: number | null;
};

const invoiceStatusLabels: Record<string, string> = {
    draft: "Draft",
    pending: "Pending",
    upcoming: "Upcoming",
    late: "Late",
    paid: "Paid",
    cancelled: "Cancelled",
    credit_note: "Credit note"
};

const injectionTypes = [
    { value: "PI", label: "Integrated program" },
    { value: "FC", label: "FC only" },
    { value: "EPP", label: "EPP only" },
    { value: "CV", label: "CV" }
] as const;

const student_progressConditions = [
    { value: "not_connected", label: "Not connected" },
    { value: "ac1_started", label: "AC1 started not validated" },
    { value: "ac1_validated", label: "AC1 validated, FC in progress" },
    { value: "ac2_not_validated", label: "AC2 not validated" }
] as const;

const cVstudent_progressConditions = [
    { value: "registration_not_confirmed", label: "Participation not confirmed" },
    { value: "all", label: "All guests" }
] as const;

const nonDPCStudentProgressConditions = [
    { value: "not_connected", label: "Not connected" },
    { value: "started_not_validated", label: "Started not validated" }
] as const;

const agents = [
    // TODO : fetch Diabolocom agents list
    { id: "1", name: "Agent 1" },
    { id: "2", name: "Agent 2" },
    { id: "3", name: "Agent 3" }
] as const;

const assignmentTypes = [
    { value: "none", label: "No assignment" },
    { value: "completion_owner", label: "Assign to CompletionOwner" },
    { value: "manual", label: "Manual assignment" }
] as const;

const caseStatusOptions = [
    { value: "New", label: "New" },
    { value: "Running", label: "Running" },
    { value: "TerminatedSession", label: "TerminatedSession" },
    { value: "Unsubscribed", label: "Unsubscribed" },
    { value: "NullOrganismPart", label: "NullOrganismPart" },
    { value: "Repositioned", label: "Repositioned" },
    { value: "NotBillable", label: "NotBillable" },
    { value: "MissingBudget", label: "MissingBudget" },
    { value: "Interrupted", label: "Interrupted" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Terminated", label: "Terminated" },
    { value: "Maintained", label: "Maintained" },
    { value: "ToTake", label: "ToTake" },
    { value: "Validated", label: "Validated" },
    { value: "Failed", label: "Failed" }
] as const;

const upsellOpportunityStageOptions = [
    { value: "New", label: "New" },
    { value: "Contacted", label: "Contacted" },
    { value: "Rappel", label: "Rappel" },
    { value: "Rappel NRP", label: "Rappel NRP" },
    { value: "Pending", label: "Pending" },
    { value: "Pause", label: "Pause" },
    { value: "Call Failed", label: "Call Failed" },
    { value: "Unreachable", label: "Unreachable" },
    { value: "Closed Lost", label: "Closed Lost" },
    { value: "Closed Won", label: "Closed Won" },
    { value: "Closed Won Other Process", label: "Closed Won Other Process" },
    { value: "Duplicate", label: "Duplicate" },
    { value: "Deprecated", label: "Deprecated" }
] as const;

const optionalNumberRange = z
    .object({
        from: z.number().optional(),
        to: z.number().optional()
    })
    .optional();

const buildFormSchema = (isUpsell: boolean) =>
    z
        .object({
            case_type: z.enum(["dpc", "cpf", "fifpl", "alma", "stripe", "gocardless", "opco"]).optional(),
            injection_type: z.enum(["PI", "FC", "EPP", "CV"]).optional(),
            student_progress: z.array(z.string()).optional(),
            dateRange: z.object({
                from: z.string().optional(),
                to: z.string().optional()
            }),
            realizedRateRange: optionalNumberRange,
            case_statuses: z.array(z.string()).optional(),
            product_code: z.string().optional(),
            organismPartRange: optionalNumberRange,
            organismListingRatioRange: optionalNumberRange,
            has_personal_funding: z.enum(["any", "true", "false"]).optional(),
            personalFundingRange: optionalNumberRange,
            sessionStartRange: z
                .object({
                    from: z.string().optional(),
                    to: z.string().optional()
                })
                .optional(),
            sessionEndRange: z
                .object({
                    from: z.string().optional(),
                    to: z.string().optional()
                })
                .optional(),
            last_injection_date: z.string().optional(),
            upsell_opportunity_stages: z.array(z.string()).optional(),
            last_contacted_date: z.string().optional(),
            last_picked_up_date: z.string().optional(),
            assignment_type: z.enum(["none", "completion_owner", "manual"], {
                required_error: "Please select an assignment type"
            }),
            assigned_agent: z.string().optional(),
            priority: z.number().min(0).max(9),
            caseIds: z.array(z.number()).optional(),
            injection_reason: z.string().optional()
        })
        .superRefine((data, ctx) => {
            if (isUpsell) {
                if (!data.injection_reason?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Injection reason is required",
                        path: ["injection_reason"]
                    });
                }
                if (!data.caseIds?.length && !hasUpsellSearchCriteria(data as FormValues)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Set at least one search filter",
                        path: ["case_statuses"]
                    });
                }
                return;
            }
            if (!data.caseIds?.length && !data.case_type) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Please fill in all fields", path: ["case_type"] });
            }
            if (data.case_type === "dpc" && !data.injection_type) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Please fill in all fields",
                    path: ["injection_type"]
                });
            }
            if (!data.caseIds?.length && !data.student_progress?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Please fill in all fields",
                    path: ["student_progress"]
                });
            }
            if (!data.caseIds?.length && !data.dateRange.from) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Please fill in all fields",
                    path: ["dateRange.from"]
                });
            }
            if (!data.caseIds?.length && !data.dateRange.to) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Please fill in all fields",
                    path: ["dateRange.to"]
                });
            }
            if (data.caseIds?.length && !data.injection_reason?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Injection reason is required",
                    path: ["injection_reason"]
                });
            }
        });

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>;

function hasUpsellSearchCriteria(formData: FormValues): boolean {
    return Boolean(
        formData.case_statuses?.length ||
            formData.product_code?.trim() ||
            formData.organismPartRange?.from != null ||
            formData.organismPartRange?.to != null ||
            formData.organismListingRatioRange?.from != null ||
            formData.organismListingRatioRange?.to != null ||
            (formData.has_personal_funding && formData.has_personal_funding !== "any") ||
            formData.personalFundingRange?.from != null ||
            formData.personalFundingRange?.to != null ||
            formData.sessionStartRange?.from ||
            formData.sessionStartRange?.to ||
            formData.sessionEndRange?.from ||
            formData.sessionEndRange?.to ||
            formData.last_injection_date ||
            formData.upsell_opportunity_stages?.length ||
            (formData.realizedRateRange?.from != null && formData.realizedRateRange.from !== 0) ||
            (formData.realizedRateRange?.to != null && formData.realizedRateRange.to !== 1)
    );
}

export function InjectionBatchForm() {
    const navigate = useNavigate();
    const fetcher = useFetcher<ActionWrapper<DiabolocomInjectionBatch>>();
    const [searchParams, _] = useSearchParams();
    const casesFetcher = useFetcher<ActionWrapper<SFCaseLoggedTimeRow[]>>();

    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [caseIds, _setCaseIds] = React.useState<number[] | undefined>(searchParams.get("caseIds") ? searchParams.get("caseIds")?.split(",").map(Number) : undefined);
    const isRecovery = searchParams.get("campaign") === "recovery";
    const isUpsell = searchParams.get("campaign") === "upsell";
    const formSchema = useMemo(() => buildFormSchema(isUpsell), [isUpsell]);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            case_type: undefined,
            injection_type: undefined,
            student_progress: undefined,
            assignment_type: isRecovery ? "completion_owner" : "none",
            assigned_agent: undefined,
            priority: 5,
            dateRange: {
                from: undefined,
                to: undefined
            },
            // Upsell shows the rate filter even with caseIds; leave unset so a hand-picked
            // batch is not silently narrowed by the Flash defaults of 0–100%.
            realizedRateRange: isUpsell ? { from: undefined, to: undefined } : { from: 0, to: 1 },
            case_statuses: undefined,
            product_code: "",
            organismPartRange: { from: undefined, to: undefined },
            organismListingRatioRange: { from: undefined, to: undefined },
            has_personal_funding: "any",
            personalFundingRange: { from: undefined, to: undefined },
            sessionStartRange: { from: undefined, to: undefined },
            sessionEndRange: { from: undefined, to: undefined },
            last_injection_date: undefined,
            upsell_opportunity_stages: undefined,
            last_contacted_date: undefined,
            last_picked_up_date: undefined,
            caseIds: undefined,
            injection_reason: ""
        }
    });

    const selectedType = form.watch("case_type");
    const isDPC = selectedType === "dpc";
    const selectedInjectionType = form.watch("injection_type");
    const isPI = isDPC && selectedInjectionType === "PI";
    const isCV = isDPC && selectedInjectionType === "CV";
    const isEPP = isDPC && selectedInjectionType === "EPP";
    const assignment_type = form.watch("assignment_type");

    useEffect(() => {
        if (caseIds) {
            form.setValue("caseIds", caseIds);
        }
        if (isRecovery) {
            form.setValue("assignment_type", "completion_owner");
        }
    }, [caseIds, isRecovery]);

    const onSubmit = () => {
        const payload = buildPayload(false);
        if (payload) {
            casesFetcher.submit(payload, {
                method: "POST",
                action: "/outils/injection-flash/cases",
                encType: "application/json"
            });
        }
    };

    const buildPayload = (attachCases: boolean = false) => {
        const formData = form.getValues();
        // Hand-picked Flash/recovery batches hide the rate inputs; their 0–1 defaults must not drop null ratios.
        // Upsell keeps the rate filter visible, so only send values the operator set.
        const sendRealizedRate = isUpsell || !caseIds;

        let payload = {
            case_type: isUpsell ? null : formData.case_type || null,
            student_progress: isUpsell ? null : formData.student_progress?.join(";") || null,
            injection_type: isUpsell ? null : (isDPC ? formData.injection_type : null) || null,
            date_range_from: isUpsell ? null : formData.dateRange.from || null,
            date_range_to: isUpsell ? null : formData.dateRange.to || null,
            realized_rate_from: sendRealizedRate ? (formData.realizedRateRange?.from ?? null) : null,
            realized_rate_to: sendRealizedRate ? (formData.realizedRateRange?.to ?? null) : null,
            assignment_type: isUpsell ? "none" : isRecovery ? "completion_owner" : formData.assignment_type,
            assigned_agent:
                !isRecovery && !isUpsell && formData.assignment_type === "manual" && formData.assigned_agent
                    ? { id: formData.assigned_agent }
                    : null,
            // Filters narrow a hand-picked batch. They never replace the caseIds from the URL.
            case_ids: caseIds || [],
            last_contacted_date: formData.last_contacted_date || null,
            last_picked_up_date: formData.last_picked_up_date || null,
            priority: formData.priority,
            injection_reason: formData.injection_reason?.trim() || null,
            ...(isUpsell
                ? {
                      campaign_id: "675",
                      case_statuses: formData.case_statuses || [],
                      product_code: formData.product_code?.trim() || null,
                      organism_part_from: formData.organismPartRange?.from ?? null,
                      organism_part_to: formData.organismPartRange?.to ?? null,
                      organism_listing_ratio_from: formData.organismListingRatioRange?.from ?? null,
                      organism_listing_ratio_to: formData.organismListingRatioRange?.to ?? null,
                      has_personal_funding:
                          formData.has_personal_funding && formData.has_personal_funding !== "any"
                              ? formData.has_personal_funding === "true"
                              : null,
                      personal_funding_from: formData.personalFundingRange?.from ?? null,
                      personal_funding_to: formData.personalFundingRange?.to ?? null,
                      session_start_from: formData.sessionStartRange?.from || null,
                      session_start_to: formData.sessionStartRange?.to || null,
                      session_end_from: formData.sessionEndRange?.from || null,
                      session_end_to: formData.sessionEndRange?.to || null,
                      last_injection_date: formData.last_injection_date || null,
                      upsell_opportunity_stages: formData.upsell_opportunity_stages || []
                  }
                : isRecovery
                  ? { campaign_id: "1874" }
                  : {})
        };
        if (attachCases) {
            payload.case_ids = casesFetcher.data?.content?.filter(c => c.to_inject).map(c => c.case.id) || [];
        }
        return payload;
    };

    const handleConfirm = () => {
        const payload = buildPayload(true);

        fetcher.submit(payload, {
            method: "POST",
            encType: "application/json"
        });
    };

    React.useEffect(() => {
        if (fetcher.data?.success && fetcher.data.content?.id) {
            setShowConfirmation(false);
            toast.success("Injection lancée avec succès");
            navigate(`/flash-injections/${fetcher.data.content?.id}/informations`);
        }
    }, [fetcher.data, navigate]);

    const getConditionLabel = (value: string) => {
        const conditions = isDPC ? student_progressConditions : nonDPCStudentProgressConditions;
        return conditions.find(c => c.value === value)?.label || value;
    };

    return (
        <div className="flex gap-6">
            <Card className="w-1/3 rounded-md border-0 shadow-md">
                <CardHeader>
                    <CardTitle>
                        {isUpsell
                            ? "New injection in the upsell généraliste campaign"
                            : isRecovery
                              ? "New injection in the recovery campaign"
                              : "New injection in Flash campaign"}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        {form.formState.errors &&
                            Object.keys(form.formState.errors).map(key => (
                                <div key={key}>
                                    {key}: {form.formState.errors[key as keyof FormValues]?.message}
                                </div>
                            ))}
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            {isUpsell ? (
                                <>
                                    <FormField
                                        control={form.control}
                                        name="injection_reason"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel required>Injection reason</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="Explain why these cases must be injected"
                                                        rows={3}
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    {caseIds && (
                                        <p>
                                            You have selected a batch of {caseIds.length} cases. Filters narrow that
                                            selection. Leave them empty to preview every selected case.
                                        </p>
                                    )}

                                    <FormField
                                        control={form.control}
                                        name="case_statuses"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Case status</FormLabel>
                                                <FormControl>
                                                    <FancyMultiSelect
                                                        options={[...caseStatusOptions]}
                                                        value={(field.value || []).join(",")}
                                                        onValueChange={value =>
                                                            field.onChange(
                                                                value
                                                                    ? value
                                                                          .split(",")
                                                                          .map(v => v.trim())
                                                                          .filter(Boolean)
                                                                    : []
                                                            )
                                                        }
                                                        placeholder="Any status"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="realizedRateRange"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-col">
                                                <FormLabel>Realized turnover / max</FormLabel>
                                                <FormControl>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <PercentageInput
                                                            value={field.value?.from}
                                                            onValueChange={value => field.onChange({ ...field.value, from: value })}
                                                            min={0}
                                                            max={100}
                                                            placeholder="From"
                                                        />
                                                        <PercentageInput
                                                            value={field.value?.to}
                                                            onValueChange={value => field.onChange({ ...field.value, to: value })}
                                                            min={0}
                                                            max={100}
                                                            placeholder="To"
                                                        />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="product_code"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Product code</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="e.g. CPF-XXX" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                        <FormField
                                            control={form.control}
                                            name="organismPartRange"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Organism part (€)</FormLabel>
                                                    <FormControl>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Input
                                                                type="number"
                                                                placeholder="From"
                                                                value={field.value?.from ?? ""}
                                                                onChange={e =>
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        from: e.target.value === "" ? undefined : Number(e.target.value)
                                                                    })
                                                                }
                                                            />
                                                            <Input
                                                                type="number"
                                                                placeholder="To"
                                                                value={field.value?.to ?? ""}
                                                                onChange={e =>
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        to: e.target.value === "" ? undefined : Number(e.target.value)
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="organismListingRatioRange"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Organism part / listing price</FormLabel>
                                                    <FormControl>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                placeholder="From"
                                                                value={field.value?.from ?? ""}
                                                                onChange={e =>
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        from: e.target.value === "" ? undefined : Number(e.target.value)
                                                                    })
                                                                }
                                                            />
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                placeholder="To"
                                                                value={field.value?.to ?? ""}
                                                                onChange={e =>
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        to: e.target.value === "" ? undefined : Number(e.target.value)
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                        <FormField
                                            control={form.control}
                                            name="has_personal_funding"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Personal funding</FormLabel>
                                                    <Select onValueChange={field.onChange} value={field.value || "any"}>
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Any" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            <SelectItem value="any">Any</SelectItem>
                                                            <SelectItem value="true">TRUE</SelectItem>
                                                            <SelectItem value="false">FALSE</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="personalFundingRange"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Personal funding amount (€)</FormLabel>
                                                    <FormControl>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Input
                                                                type="number"
                                                                placeholder="From"
                                                                value={field.value?.from ?? ""}
                                                                onChange={e =>
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        from: e.target.value === "" ? undefined : Number(e.target.value)
                                                                    })
                                                                }
                                                            />
                                                            <Input
                                                                type="number"
                                                                placeholder="To"
                                                                value={field.value?.to ?? ""}
                                                                onChange={e =>
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        to: e.target.value === "" ? undefined : Number(e.target.value)
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                        <FormField
                                            control={form.control}
                                            name="sessionStartRange"
                                            render={({ field }) => {
                                                const from = field.value?.from ? DateTime.fromISO(field.value.from) : undefined;
                                                const to = field.value?.to ? DateTime.fromISO(field.value.to) : undefined;
                                                const range = from && to ? { from, to } : undefined;
                                                return (
                                                    <FormItem className="flex flex-col">
                                                        <FormLabel>Session start date</FormLabel>
                                                        <FormControl>
                                                            <DateRangePicker
                                                                range={range}
                                                                onRangeChange={next => {
                                                                    field.onChange(
                                                                        next?.from && next?.to
                                                                            ? {
                                                                                  from: next.from.toISODate() ?? "",
                                                                                  to: next.to.toISODate() ?? ""
                                                                              }
                                                                            : { from: undefined, to: undefined }
                                                                    );
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="sessionEndRange"
                                            render={({ field }) => {
                                                const from = field.value?.from ? DateTime.fromISO(field.value.from) : undefined;
                                                const to = field.value?.to ? DateTime.fromISO(field.value.to) : undefined;
                                                const range = from && to ? { from, to } : undefined;
                                                return (
                                                    <FormItem className="flex flex-col">
                                                        <FormLabel>Session end date</FormLabel>
                                                        <FormControl>
                                                            <DateRangePicker
                                                                range={range}
                                                                onRangeChange={next => {
                                                                    field.onChange(
                                                                        next?.from && next?.to
                                                                            ? {
                                                                                  from: next.from.toISODate() ?? "",
                                                                                  to: next.to.toISODate() ?? ""
                                                                              }
                                                                            : { from: undefined, to: undefined }
                                                                    );
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name="last_injection_date"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Exclude last injection since</FormLabel>
                                                <FormControl>
                                                    <DatePicker
                                                        className="w-full"
                                                        date={field.value ? DateTime.fromISO(field.value) : undefined}
                                                        setDate={d => field.onChange(d ? d.toISODate() : undefined)}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="upsell_opportunity_stages"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Status of the opportunity already injected</FormLabel>
                                                <FormControl>
                                                    <FancyMultiSelect
                                                        options={[...upsellOpportunityStageOptions]}
                                                        value={(field.value || []).join(",")}
                                                        onValueChange={value =>
                                                            field.onChange(
                                                                value
                                                                    ? value
                                                                          .split(",")
                                                                          .map(v => v.trim())
                                                                          .filter(Boolean)
                                                                    : []
                                                            )
                                                        }
                                                        placeholder="Any upsell opportunity stage"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </>
                            ) : !caseIds ? (
                                <>
                                    <FormField
                                        control={form.control}
                                        name="case_type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Type</FormLabel>
                                                <Select
                                                    onValueChange={v => {
                                                        field.onChange(v);
                                                        form.setValue("injection_type", undefined);
                                                        form.setValue("student_progress", undefined);
                                                    }}
                                                    defaultValue={field.value || undefined}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select a type" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="dpc">DPC</SelectItem>
                                                        <SelectItem value="cpf">CPF</SelectItem>
                                                        <SelectItem value="fifpl">FIFPL</SelectItem>
                                                        <SelectItem value="alma">Alma</SelectItem>
                                                        <SelectItem value="stripe">Stripe</SelectItem>
                                                        <SelectItem value="gocardless">GoCardless</SelectItem>
                                                        <SelectItem value="opco">OPCO</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {isDPC && (
                                        <FormField
                                            control={form.control}
                                            name="injection_type"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Training structure followed</FormLabel>
                                                    <Select
                                                        onValueChange={v => {
                                                            field.onChange(v);
                                                            form.setValue("student_progress", undefined);
                                                        }}
                                                        defaultValue={field.value || undefined}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Select an injection type" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {injectionTypes.map(type => (
                                                                <SelectItem key={type.value} value={type.value}>
                                                                    {type.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}

                                    <FormField
                                        control={form.control}
                                        name="student_progress"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Progress conditions</FormLabel>
                                                <div className="space-y-2">
                                                    {(isPI || isEPP ? student_progressConditions : isCV ? cVstudent_progressConditions : nonDPCStudentProgressConditions).map(condition => (
                                                        <div key={condition.value} className="flex items-center space-x-2 text-sm">
                                                            <Checkbox
                                                                id={condition.value}
                                                                checked={field.value?.includes(condition.value)}
                                                                onCheckedChange={checked => {
                                                                    const newValue = checked ? [...(field.value || []), condition.value] : field.value?.filter(v => v !== condition.value) || [];
                                                                    field.onChange(newValue);
                                                                }}
                                                            />
                                                            <label htmlFor={condition.value}>{condition.label}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                        <FormField
                                            control={form.control}
                                            name="dateRange"
                                            render={({ field }) => {
                                                const from = field.value.from ? DateTime.fromISO(field.value.from) : undefined;
                                                const to = field.value.to ? DateTime.fromISO(field.value.to) : undefined;
                                                const range = from && to ? { from, to } : undefined;
                                                return (
                                                    <FormItem className="flex flex-col">
                                                        <FormLabel>{isCV ? "Virtual class date" : "Next Completion Deadline"} </FormLabel>
                                                        <FormControl>
                                                            <DateRangePicker
                                                                range={range}
                                                                onRangeChange={range => {
                                                                    if (range && range.from && range.to) {
                                                                        field.onChange({
                                                                            from: range.from.toISODate() ?? "",
                                                                            to: range.to.toISODate() ?? ""
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="realizedRateRange"
                                            render={({ field }) => {
                                                return (
                                                    <FormItem className="flex flex-col">
                                                        <FormLabel>Realized turnover / max</FormLabel>
                                                        <FormControl>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <PercentageInput
                                                                    value={field.value?.from || 0}
                                                                    onValueChange={value => field.onChange({ ...field.value, from: value })}
                                                                    min={0}
                                                                    max={100}
                                                                />
                                                                <PercentageInput
                                                                    value={field.value?.to || 1}
                                                                    onValueChange={value => field.onChange({ ...field.value, to: value })}
                                                                    min={0}
                                                                    max={100}
                                                                />
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                );
                                            }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p>You have selected a batch of {caseIds.length} cases</p>
                                    <FormField
                                        control={form.control}
                                        name="injection_reason"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel required>Injection reason</FormLabel>
                                                <FormControl>
                                                    <Textarea placeholder="Explain why these cases must be injected" rows={3} {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </>
                            )}

                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                <FormField
                                    control={form.control}
                                    name="last_contacted_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Exclude last contacted since</FormLabel>
                                            <FormControl>
                                                <DatePicker
                                                    className="w-full"
                                                    date={field.value ? DateTime.fromISO(field.value) : undefined}
                                                    setDate={d => field.onChange(d ? d.toISODate() : undefined)}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="last_picked_up_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Exclude last picked-up since</FormLabel>
                                            <FormControl>
                                                <DatePicker
                                                    className="w-full"
                                                    date={field.value ? DateTime.fromISO(field.value) : undefined}
                                                    setDate={d => field.onChange(d ? d.toISODate() : undefined)}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="assignment_type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Assignment type</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value} disabled={isRecovery || isUpsell}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select an assignment type" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {assignmentTypes.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {assignment_type === "manual" && (
                                <FormField
                                    control={form.control}
                                    name="assigned_agent"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Assigned agent</FormLabel>
                                            <div className="flex space-x-1">
                                                <Combobox
                                                    name="owner_id"
                                                    optionsUrl="/outils/injection-flash/picklists/agents"
                                                    optionFormatter={(agent: UserType) => {
                                                        return { value: agent.id.toString() || "0", label: display_user(agent, true) };
                                                    }}
                                                    placeholder="Select an agent"
                                                    className="w-[275px] justify-between"
                                                    onValueChange={value => {
                                                        if (value) {
                                                            field.onChange(value);
                                                        } else {
                                                            field.onChange(null);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            {!isUpsell && (
                            <FormField
                                control={form.control}
                                name="priority"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Injection priority (0 = maximum, 9 = minimum)</FormLabel>
                                        <Select onValueChange={value => field.onChange(parseInt(value))} defaultValue={field.value.toString()}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a priority" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="0">0 - Maximum</SelectItem>
                                                <SelectItem value="1">1 - Very high</SelectItem>
                                                <SelectItem value="2">2 - High</SelectItem>
                                                <SelectItem value="3">3 - Quite high</SelectItem>
                                                <SelectItem value="4">4 - Medium</SelectItem>
                                                <SelectItem value="5">5 - Quite medium</SelectItem>
                                                <SelectItem value="6">6 - Low</SelectItem>
                                                <SelectItem value="7">7 - Quite low</SelectItem>
                                                <SelectItem value="8">8 - Very low</SelectItem>
                                                <SelectItem value="9">9 - Minimum</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            )}

                            <Button type="submit" disabled={casesFetcher.state !== "idle"}>
                                <NavigationStatus fetcher={casesFetcher}>View sessions</NavigationStatus>
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            <Card className="w-2/3 rounded-md border-0 shadow-md">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>
                            Preview of injection conditions
                            {casesFetcher.data?.content && (
                                <Badge variant="outline" className="ml-2">
                                    {casesFetcher.data.content.filter(c => c.to_inject).length} session{casesFetcher.data.content.filter(c => c.to_inject).length > 1 ? "s" : ""}
                                </Badge>
                            )}
                        </CardTitle>
                        {casesFetcher.data?.content && (
                            <Button variant="default" onClick={() => setShowConfirmation(true)} disabled={casesFetcher.state !== "idle" || !casesFetcher.data.content.some(c => c.to_inject)}>
                                Inject
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {casesFetcher.data?.content === undefined ? (
                        <p className="text-muted-foreground">Select injection criteria to view cases</p>
                    ) : casesFetcher.data?.content && casesFetcher.data.content.length > 0 ? (
                        <DataTable
                            columns={[
                                {
                                    accessorKey: "to_inject",
                                    header: "To inject",
                                    cell: ({ row }) => (
                                        <span className={row.original.to_inject ? undefined : "text-amber-500"}>
                                            {row.original.to_inject ? "Yes" : "No"}
                                        </span>
                                    )
                                },
                                {
                                    id: "comment",
                                    header: "Motif",
                                    cell: ({ row }) => (row.original.to_inject ? "" : row.original.comment || "")
                                },
                                {
                                    accessorKey: "subject",
                                    header: "Subject",
                                    accessorFn: row => row.case.subject,
                                    cell: ({ row }) => (
                                        <a href={`/cases/${row.original.case.id}`} target="_blank" className="text-blue-500">
                                            <WithIcon icon={ExternalLink} trailing>
                                                {row.original.case.subject}
                                            </WithIcon>
                                        </a>
                                    )
                                },
                                {
                                    id: "session",
                                    header: "Dates",
                                    cell: ({ row }) => (
                                        <span className="space-x-1">
                                            <span>{row.original.case.starting_date ? new Date(row.original.case.starting_date).toLocaleDateString() : "-"}</span>
                                            <span>-</span>
                                            <span>{row.original.case.ending_date ? new Date(row.original.case.ending_date).toLocaleDateString() : "-"}</span>
                                        </span>
                                    )
                                },
                                ...(isUpsell
                                    ? ([] as ColumnDef<SFCaseLoggedTimeRow>[])
                                    : isRecovery
                                    ? ([
                                          {
                                              id: "invoice_status",
                                              header: "Invoice status",
                                              cell: ({ row }) => invoiceStatusLabels[row.original.invoice_status || ""] || row.original.invoice_status || "-"
                                          },
                                          {
                                              id: "estimated_payment_date",
                                              header: "Estimated payment date",
                                              cell: ({ row }) => (row.original.case.estimated_paiement_date ? new Date(row.original.case.estimated_paiement_date).toLocaleDateString() : "-")
                                          },
                                          {
                                              id: "amount_to_recover",
                                              header: "RAC to recover",
                                              cell: ({ row }) =>
                                                  row.original.amount_to_recover == null ? "-" : `${row.original.amount_to_recover.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`
                                          },
                                          {
                                              id: "picked_up",
                                              header: "Picked up",
                                              cell: ({ row }) => (row.original.case.picked_up ? "Yes" : "No")
                                          },
                                          {
                                              id: "last_contacted",
                                              header: "Last contacted",
                                              cell: ({ row }) => {
                                                  const value = row.original.case.last_contacted_date as string | null;
                                                  return value ? new Date(value).toLocaleString() : "-";
                                              }
                                          }
                                      ] as ColumnDef<SFCaseLoggedTimeRow>[])
                                    : ([
                                          {
                                              accessorKey: "next_completion_deadline",
                                              cell: ({ row }) => {
                                                  const date = isCV && row.original.case.cv?.starting_date ? row.original.case.cv.starting_date : row.original.case.next_completion_deadline;
                                                  return <span className="font-semibold">{date ? new Date(date).toLocaleDateString() : "-"}</span>;
                                              }
                                          },
                                          ...(form.watch("case_type") === "dpc" && form.watch("injection_type") === "PI"
                                              ? ([
                                                    {
                                                        id: "ac1",
                                                        header: "AC1",
                                                        cell: ({ row }) => (
                                                            <span
                                                                className={cn(
                                                                    row.original.case.ac1?.validated
                                                                        ? "text-green-500"
                                                                        : (row.original.case.ac1?.logged_time || 0) > 0
                                                                          ? "text-amber-500"
                                                                          : "text-red-500"
                                                                )}
                                                            >
                                                                {secondsToHMS(row.original.case.ac1?.logged_time || 0)}
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        id: "fc",
                                                        header: "FC",
                                                        cell: ({ row }) => (
                                                            <span
                                                                className={cn(
                                                                    row.original.case.fc?.validated
                                                                        ? "text-green-500"
                                                                        : (row.original.case.fc?.logged_time || 0) > 0
                                                                          ? "text-amber-500"
                                                                          : "text-red-500"
                                                                )}
                                                            >
                                                                {secondsToHMS(row.original.case.fc?.logged_time || 0)}
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        id: "ac2",
                                                        header: "AC2",
                                                        cell: ({ row }) => (
                                                            <span
                                                                className={cn(
                                                                    row.original.case.ac2?.validated
                                                                        ? "text-green-500"
                                                                        : (row.original.case.ac2?.logged_time || 0) > 0
                                                                          ? "text-amber-500"
                                                                          : "text-red-500"
                                                                )}
                                                            >
                                                                {secondsToHMS(row.original.case.ac2?.logged_time || 0)}
                                                            </span>
                                                        )
                                                    }
                                                ] as ColumnDef<SFCaseLoggedTimeRow>[])
                                              : form.watch("case_type") === "dpc" && form.watch("injection_type") === "EPP"
                                                ? ([
                                                      {
                                                          id: "ac1",
                                                          header: "AC1",
                                                          cell: ({ row }) => (
                                                              <span
                                                                  className={cn(
                                                                      row.original.case.ac1?.validated
                                                                          ? "text-green-500"
                                                                          : (row.original.case.ac1?.logged_time || 0) > 0
                                                                            ? "text-amber-500"
                                                                            : "text-red-500"
                                                                  )}
                                                              >
                                                                  {secondsToHMS(row.original.case.ac1?.logged_time || 0)}
                                                              </span>
                                                          )
                                                      },
                                                      {
                                                          id: "cv_logged_time",
                                                          header: "CV",
                                                          cell: ({ row }) => (
                                                              <span
                                                                  className={cn(
                                                                      row.original.case.cv?.validated
                                                                          ? "text-green-500"
                                                                          : (row.original.case.cv?.logged_time || 0) > 0
                                                                            ? "text-amber-500"
                                                                            : "text-red-500"
                                                                  )}
                                                              >
                                                                  {secondsToHMS(row.original.case.cv?.logged_time || 0)}
                                                              </span>
                                                          )
                                                      },
                                                      {
                                                          id: "ac2_logged_time",
                                                          header: "AC2",
                                                          cell: ({ row }) => (
                                                              <span
                                                                  className={cn(
                                                                      row.original.case.ac2?.validated
                                                                          ? "text-green-500"
                                                                          : (row.original.case.ac2?.logged_time || 0) > 0
                                                                            ? "text-amber-500"
                                                                            : "text-red-500"
                                                                  )}
                                                              >
                                                                  {secondsToHMS(row.original.case.ac2?.logged_time || 0)}
                                                              </span>
                                                          )
                                                      }
                                                  ] as ColumnDef<SFCaseLoggedTimeRow>[])
                                                : ([
                                                      {
                                                          id: "walter_session_time",
                                                          header: "Time",
                                                          cell: ({ row }) => `${secondsToHMS(row.original.case.walter_session_time || 0)}`
                                                      }
                                                  ] as ColumnDef<SFCaseLoggedTimeRow>[])),
                                          {
                                              id: "progression",
                                              header: "Realized turnover / max",
                                              cell: ({ row }) => `${Math.round(((row.original.case.realized_turnover || 0) / (row.original.case.max_realized_turnover || 1)) * 100)}%`
                                          }
                                      ] as ColumnDef<SFCaseLoggedTimeRow>[]))
                            ]}
                            data={casesFetcher.data.content}
                            bodyClassName="h-auto"
                            filters={[
                                {
                                    type: "input",
                                    placeholder: "Search a case..."
                                }
                            ]}
                        />
                    ) : (
                        <p className="text-muted-foreground">No session found with the selected criteria</p>
                    )}
                </CardContent>
            </Card>

            <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm injection</DialogTitle>
                        <DialogDescription>Please check the selected injection conditions:</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <FormError errorMessage={fetcher.data?.message} className="mb-4" />
                        {isUpsell ? (
                            <>
                                <div>
                                    <strong>This batch targets the upsell généraliste campaign</strong>
                                </div>
                                {caseIds && (
                                    <div>
                                        <strong>Injection from a custom batch of {caseIds.length} cases</strong>
                                    </div>
                                )}
                                {form.watch("injection_reason") && (
                                    <div>
                                        <strong>Injection reason:</strong> {form.watch("injection_reason")}
                                    </div>
                                )}
                                {!!form.watch("case_statuses")?.length && (
                                    <div>
                                        <strong>Case status:</strong> {form.watch("case_statuses")?.join(", ")}
                                    </div>
                                )}
                                {(form.watch("realizedRateRange")?.from != null || form.watch("realizedRateRange")?.to != null) && (
                                    <div>
                                        <strong>Realized turnover / max:</strong> {form.watch("realizedRateRange")?.from ?? "–"} →{" "}
                                        {form.watch("realizedRateRange")?.to ?? "–"}
                                    </div>
                                )}
                                {form.watch("product_code") && (
                                    <div>
                                        <strong>Product code:</strong> {form.watch("product_code")}
                                    </div>
                                )}
                            </>
                        ) : !caseIds ? (
                            <>
                                <div>
                                    <strong>Type :</strong> {form.watch("case_type")?.toUpperCase()}
                                </div>
                                {isDPC && form.watch("injection_type") && (
                                    <div>
                                        <strong>Training structure followed :</strong> {form.watch("injection_type")}
                                    </div>
                                )}
                                <div>
                                    <strong>Progress conditions :</strong>
                                    <ul className="mt-1 list-disc pl-4">{form.watch("student_progress")?.map(condition => <li key={condition}>{getConditionLabel(condition)}</li>)}</ul>
                                </div>
                                <div>
                                    <strong>Period :</strong> from {form.watch("dateRange.from")} to {form.watch("dateRange.to")}
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <strong>Injection from a custom batch of {caseIds.length} cases</strong>
                                </div>
                                {isRecovery && (
                                    <div>
                                        <strong>This batch targets the recovery campaign</strong>
                                    </div>
                                )}
                                <div>
                                    <strong>Injection reason:</strong> {form.watch("injection_reason")}
                                </div>
                            </>
                        )}

                        {form.watch("last_contacted_date") && (
                            <div>
                                <strong>Exclude last contacted since :</strong> {form.watch("last_contacted_date")}
                            </div>
                        )}

                        {form.watch("last_picked_up_date") && (
                            <div>
                                <strong>Exclude last picked-up since :</strong> {form.watch("last_picked_up_date")}
                            </div>
                        )}
                        <Separator />

                        <div>
                            <strong>
                                {casesFetcher.data?.content &&
                                    `${casesFetcher.data?.content?.filter(c => c.to_inject).length} case${(casesFetcher.data?.content?.filter(c => c.to_inject).length || 0) > 1 ? "s" : ""}`}{" "}
                                are going to be injected into the{" "}
                                {isUpsell ? "upsell généraliste" : isRecovery ? "recovery" : "flash"} campaign with the
                                following configuration :
                            </strong>
                        </div>
                        <div>
                            <strong>Assignment type :</strong> {assignmentTypes.find(t => t.value === form.watch("assignment_type"))?.label}
                        </div>
                        {form.watch("assignment_type") === "manual" && form.watch("assigned_agent") && (
                            <div>
                                <strong>Assigned agent :</strong> {agents.find(a => a.id === form.watch("assigned_agent"))?.name}
                            </div>
                        )}
                        {!isUpsell && (
                        <div>
                            <strong>Priority :</strong>{" "}
                            {form.watch("priority") === 0
                                ? "Maximum (0)"
                                : form.watch("priority") <= 3
                                  ? "High"
                                  : form.watch("priority") <= 6
                                    ? "Medium"
                                    : form.watch("priority") < 9
                                      ? "Low"
                                      : "Minimum (9)"}
                        </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmation(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirm} disabled={fetcher.state !== "idle" || casesFetcher.state !== "idle" || !casesFetcher.data?.content?.some(c => c.to_inject)}>
                            <NavigationStatus fetcher={fetcher}>Start injection</NavigationStatus>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
